import axios from "axios";
import { toast } from "sonner";
import { enqueue as enqueueOffline, OfflineQueuedError } from "@/lib/offlineQueue";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ----------------------------------------------------------------------
// Reference-data cache (iter 45).
// The service worker already caches GET /api/* responses, but on a FIRST
// offline load (fresh browser, no SW cache yet) forms like New Transaction
// would find their categories/accounts dropdowns empty and block submission,
// making the offline write-queue unreachable. As a belt-and-suspenders layer
// we mirror a handful of small reference endpoints to localStorage; when the
// live GET fails we transparently return the cached copy.
// ----------------------------------------------------------------------
const REF_CACHE_PATHS = ["/categories", "/accounts", "/entities", "/coa", "/tax-rates"];
const REF_CACHE_KEY_PREFIX = "ledger.ref-cache:";
// SEC-001 hardening: reference-cache TTL. 12h is short enough that renamed
// categories/accounts won't stay stale forever, but long enough to survive a
// full offline workday.
const REF_CACHE_TTL_MS = 12 * 60 * 60 * 1000;

function _refCacheKeyForConfig(cfg) {
  try {
    const u = cfg.url || "";
    // Only cache the exact reference paths (ignore query strings for simplicity).
    for (const p of REF_CACHE_PATHS) {
      if (u === p || u.startsWith(p + "?")) return REF_CACHE_KEY_PREFIX + p;
    }
  } catch { /* ignore */ }
  return null;
}

/**
 * SEC-001: called by AuthContext on logout to purge every trace of the
 * previous user's data from the browser. Wipes:
 *   * localStorage reference cache (categories/accounts/etc.)
 *   * IndexedDB offline write queue (via dynamic import to avoid a cycle)
 *   * Service-Worker API cache (async best-effort)
 */
export async function wipeClientCaches() {
  try {
    // localStorage — ref cache
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(REF_CACHE_KEY_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch { /* private mode / storage disabled */ }
  try {
    const { clearAll } = await import("@/lib/offlineQueue");
    await clearAll();
  } catch { /* IndexedDB unavailable */ }
  try {
    // Message SW to purge its API cache
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage("CLEAR_CACHES");
    }
  } catch { /* SW unavailable */ }
}

// Read the currently-selected entity from sessionStorage and attach as header.
// sessionStorage (vs localStorage) auto-clears when the tab closes — reduces
// stale-state leakage on shared machines.
api.interceptors.request.use((config) => {
  try {
    const eid = sessionStorage.getItem("ledger.entity_id");
    if (eid) {
      config.headers = config.headers || {};
      config.headers["X-Entity-Id"] = eid;
    }
  } catch (err) {
    // Storage disabled (private mode) — request still succeeds without entity scope.
    console.debug("[api] sessionStorage read failed:", err?.message);
  }
  return config;
});

// True if the request URL points to an auth endpoint. We NEVER queue those.
// iter 71c fix: axios passes `cfg.url` WITHOUT the `/api` baseURL prefix
// (so login shows up as `/auth/login`, not `/api/auth/login`). Match either
// form so a temporary network blip on login never gets misfiled into the
// offline write-queue with a bogus "Saved offline" toast.
function isAuthPath(url) {
  return typeof url === "string" && /(^|\/)(api\/)?auth(\/|$)/.test(url);
}
function isMutation(method) {
  const m = (method || "get").toLowerCase();
  return m === "post" || m === "patch" || m === "put" || m === "delete";
}
function isNetworkError(err) {
  // No response = fetch failed (offline, DNS, CORS preflight lost, etc.).
  // Also treat the SW's synthesized 503 {offline:true} as a network error.
  if (!err?.response) return true;
  const body = err.response.data;
  return err.response.status === 503 && body && body.offline;
}

// Response interceptor:
//   1. Mirror successful reference-data GETs to localStorage.
//   2. On a failed reference-data GET, transparently serve the cached copy.
//   3. Enqueue mutations that failed due to a network error (offline).
//   4. Surface a friendly `detail` for the SW's offline JSON.
api.interceptors.response.use(
  (r) => {
    // Mirror reference data to localStorage on successful reads.
    try {
      const key = _refCacheKeyForConfig(r.config || {});
      if (key && r.status === 200) {
        localStorage.setItem(key, JSON.stringify({ at: Date.now(), data: r.data }));
      }
    } catch { /* quota/private-mode — ignore */ }
    return r;
  },
  async (err) => {
    const cfg = err?.config || {};
    // (2) Reference-data GET fell back — try localStorage.
    if ((cfg.method || "get").toLowerCase() === "get" && isNetworkError(err)) {
      const key = _refCacheKeyForConfig(cfg);
      if (key) {
        try {
          const raw = localStorage.getItem(key);
          if (raw) {
            const cached = JSON.parse(raw);
            // TTL check — refuse very stale cache to prevent showing renamed
            // categories/accounts for weeks after the user last logged in.
            if (cached.at && (Date.now() - cached.at) < REF_CACHE_TTL_MS) {
              return Promise.resolve({
                data: cached.data,
                status: 200,
                statusText: "OK (offline cache)",
                headers: {},
                config: cfg,
                request: err.request,
              });
            }
            // Expired — drop it.
            try { localStorage.removeItem(key); } catch { /* ignore */ }
          }
        } catch { /* fall through */ }
      }
    }
    // Already a replay attempt → don't recurse.
    const replay = cfg.headers && cfg.headers["X-Offline-Replay"];
    if (!replay && isMutation(cfg.method) && !isAuthPath(cfg.url) && isNetworkError(err)) {
      try {
        const entityId = (cfg.headers && cfg.headers["X-Entity-Id"]) || null;
        const label = `${(cfg.method || "").toUpperCase()} ${cfg.url}`;
        // SEC-001: stamp the current user id so drain refuses to replay this
        // item under someone else's session on a shared device.
        let userId = null;
        try { userId = sessionStorage.getItem("ledger.user_id") || null; } catch { /* ignore */ }
        const queueId = await enqueueOffline({
          method: cfg.method || "post",
          url: cfg.url || "",
          data: cfg.data ? (typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data) : null,
          entityId,
          userId,
          label,
        });
        try {
          toast.info("Saved offline — will sync when you're back online", {
            duration: 3500,
            id: "offline-queued",  // dedupe if user makes several writes in a row
          });
        } catch { /* toast optional */ }
        return Promise.reject(new OfflineQueuedError({ label, queueId }));
      } catch (queueErr) {
        // Queueing itself failed (IndexedDB disabled?) — fall through and
        // reject with the original error so the UI still shows something.
        console.warn("[api] offline enqueue failed:", queueErr?.message);
      }
    }
    const body = err?.response?.data;
    if (body && body.offline && !body.detail) {
      err.response.data = {
        ...body,
        detail: body.error || "You're offline. Changes weren't saved.",
      };
    }
    return Promise.reject(err);
  },
);

export default api;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
